"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { EntityAbout } from "@/components/catalog-entity/entity-about";
import { EntityAboutMore } from "@/components/catalog-entity/entity-about-more";
import type { EntityAboutSection } from "@/components/catalog-entity/entity-about-section";
import { useLinksAboutSection } from "@/components/catalog-entity/use-links-about-section";
import { Link } from "@/components/link";
import type { CatalogUrl, SocialLink } from "@garimpa/lib/musicTypes";

import { CompanyChildCompanies } from "./company-child-companies";
import { getCompanyChildCompaniesQueryOptions } from "./company-child-companies-query";

const CHILD_PREVIEW = 4;

interface CompanyAboutProps {
  companyId: string;
  companyName: string;
  officialUrls: CatalogUrl[];
  socialUrls: SocialLink[];
  otherUrls: CatalogUrl[];
  sourceLinks?: ReadonlyArray<{ source: string; label: string; url: string }>;
}

/** Related organizations and links for the company page's About cards and panel. */
export function CompanyAbout({
  companyId,
  companyName,
  officialUrls,
  socialUrls,
  otherUrls,
  sourceLinks,
}: CompanyAboutProps) {
  const t = useTranslations("label");
  const { data, isLoading } = useQuery(getCompanyChildCompaniesQueryOptions(companyId));
  const childCompanies = data ?? [];
  const links = useLinksAboutSection({ officialUrls, socialUrls, otherUrls, sourceLinks });
  const previewChildren = childCompanies.slice(0, CHILD_PREVIEW);

  const sections: EntityAboutSection[] = [
    {
      key: "related",
      label: t("childLabels"),
      count: childCompanies.length,
      loading: isLoading,
      preview: isLoading ? (
        <span className="h-4 w-40 animate-pulse rounded bg-secondary" />
      ) : (
        <>
          {previewChildren.map((child) => (
            <Link
              key={child.id}
              to={`/company/${child.id}`}
              className="text-foreground/85 hover:text-primary"
            >
              {child.name}
            </Link>
          ))}
          <EntityAboutMore count={childCompanies.length - previewChildren.length} />
        </>
      ),
      content: <CompanyChildCompanies companyId={companyId} childCompanies={childCompanies} />,
    },
    links,
  ];

  return <EntityAbout id={companyId} entityName={companyName} sections={sections} />;
}
